import { useEffect, useState } from 'react';
import {
  knowledgeGroups,
  knowledgeEntity,
  knowledgeTopicsKeywords,
} from '@/mocks/knowledgeCatalogue';

const questionAnchor = (question: string, fallback: number): string => {
  const match = question.match(/^(\d+)\./);
  return `q-${match ? match[1] : fallback}`;
};

export default function KnowledgeCatalogue() {
  const [openId, setOpenId] = useState<string | null>(null);
  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>(() => {
    const initial: Record<string, boolean> = {};
    knowledgeGroups.forEach((group) => {
      initial[group.id] = true;
    });
    return initial;
  });

  useEffect(() => {
    const hash = window.location.hash.replace('#', '');
    if (!hash.startsWith('q-')) return;
    for (const group of knowledgeGroups) {
      const hit = group.faqs.some(
        (faq, index) => questionAnchor(faq.question, index) === hash
      );
      if (hit) {
        setOpenGroups((prev) => ({ ...prev, [group.id]: true }));
        setOpenId(hash);
        break;
      }
    }
  }, []);

  const toggleGroup = (id: string) => {
    setOpenGroups((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const toggleFaq = (key: string) => {
    setOpenId((prev) => (prev === key ? null : key));
  };

  return (
    <div>
      <div className="space-y-4">
        {knowledgeGroups.map((group) => {
          const isGroupOpen = Boolean(openGroups[group.id]);
          return (
            <div
              key={group.id}
              id={group.id}
              className="scroll-mt-28 rounded-xl border border-royal-navy/40 bg-royal-navy/10 overflow-hidden"
            >
              <h2 className="text-lg md:text-xl">
                <button
                  type="button"
                  onClick={() => toggleGroup(group.id)}
                  aria-expanded={isGroupOpen}
                  aria-controls={`${group.id}-faqs`}
                  className="w-full flex items-center justify-between gap-4 text-left px-[18px] py-4 text-warm-white hover:text-champagne-gold transition-colors duration-300 cursor-pointer"
                  style={{ fontFamily: "'Playfair Display', serif" }}
                >
                  <span className="font-semibold leading-snug">{group.title}</span>
                  <span className="shrink-0 w-6 h-6 flex items-center justify-center text-champagne-gold">
                    <i
                      className={`ri-arrow-down-s-line text-xl transition-transform duration-300 ${
                        isGroupOpen ? 'rotate-180' : ''
                      }`}
                    />
                  </span>
                </button>
              </h2>
              <div
                id={`${group.id}-faqs`}
                className={`px-[18px] pb-4 ${
                  isGroupOpen ? 'space-y-2.5 animate-fade-in' : 'hidden'
                }`}
              >
                {group.faqs.map((faq, fIndex) => {
                  const key = questionAnchor(faq.question, fIndex);
                  const isOpen = openId === key;
                  return (
                    <div
                      key={key}
                      id={key}
                      className={`scroll-mt-28 rounded-lg border transition-colors duration-300 ${
                        isOpen
                          ? 'border-champagne-gold/50 bg-royal-navy/25'
                          : 'border-royal-navy/40 bg-royal-navy/15'
                      }`}
                    >
                      <h3 className="text-sm md:text-base">
                        <button
                          type="button"
                          onClick={() => toggleFaq(key)}
                          aria-expanded={isOpen}
                          aria-controls={`${key}-answer`}
                          className="w-full flex items-start justify-between gap-4 text-left px-[18px] py-4 cursor-pointer"
                        >
                          <span className="text-warm-white font-medium leading-relaxed">
                            {faq.question}
                          </span>
                          <span className="shrink-0 w-6 h-6 flex items-center justify-center text-champagne-gold">
                            <i
                              className={`ri-${
                                isOpen ? 'subtract-line' : 'add-line'
                              } text-lg`}
                            />
                          </span>
                        </button>
                      </h3>
                      <p
                        id={`${key}-answer`}
                        className={`px-[18px] pb-4 text-silver-grey leading-relaxed text-sm md:text-base ${
                          isOpen ? 'block animate-fade-in' : 'hidden'
                        }`}
                      >
                        {faq.answer}
                      </p>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>

      <div className="border-t border-royal-navy/30 mt-12 pt-6">
        <p className="text-taupe-grey text-sm leading-relaxed mb-3">
          {knowledgeEntity.map((item) => (
            <span key={item.label}>
              <strong className="text-silver-grey font-semibold">
                {item.label}:
              </strong>{' '}
              {item.value}
              <span className="text-royal-navy/60 px-2">•</span>
            </span>
          ))}
        </p>
        <p className="text-taupe-grey text-sm leading-relaxed">
          Topics: {knowledgeTopicsKeywords}
        </p>
      </div>
    </div>
  );
}